import { create } from "zustand";
import type { MessagePayload } from "firebase/messaging";
import { useAuthStore } from "./authStore";
import {
  getLocalizedText,
  notiDeeplinkToPath,
  notiHrefToPath,
  NOTIFICATIONS_FALLBACK_PATH,
  type RemoteNoti,
} from "../api/notifications";
import { playChatSound } from "../lib/notificationSound";
import { hasMessageContent } from "../utils/liveMessage";

export interface NotifToast {
  id: string;
  conv_id: string;
  conv_type: string;
  senderName: string;
  text: string;
  navigateTo: string;
  count: number;
}

export type NotifSection = "pairChat" | "streetChat";

// '0' is the legacy numeric string for GROUP sent by older backend versions
// Must mirror STREET_CHAT_TYPES in public/sw-notification-routing.js
export const STREET_CHAT_TYPES = new Set([
  "GROUP",
  "TEMPORARY_SC",
  "TEMPORARY",
  "0",
]);

function resolveSection(conv_type: string): NotifSection {
  return STREET_CHAT_TYPES.has(conv_type) ? "streetChat" : "pairChat";
}

function resolveNavigateTo(conv_type: string, conv_id: string): string {
  if (STREET_CHAT_TYPES.has(conv_type))
    return conv_id
      ? `/street-chat?conv=${encodeURIComponent(conv_id)}`
      : "/street-chat";
  return conv_id ? `/pair-chat/${conv_id}` : "/pair-chat";
}

function isSuppressed(
  conv_type: string,
  conv_id: string,
  pathname: string,
): boolean {
  if (pathname === "/street-chat" && STREET_CHAT_TYPES.has(conv_type))
    return true;
  const match = pathname.match(/^\/pair-chat\/(.+)$/);
  if (match && match[1] === conv_id) return true;
  return false;
}

function sameGroup(
  toast: NotifToast,
  conv_id: string,
  conv_type: string,
): boolean {
  if (resolveSection(toast.conv_type) !== resolveSection(conv_type))
    return false;
  return conv_id !== "" && toast.conv_id === conv_id;
}

interface NotificationState {
  toasts: NotifToast[];
  badges: Record<NotifSection, number>;
  dispatch: (payload: MessagePayload, pathname: string) => void;
  dispatchAppNoti: (noti: RemoteNoti, pathname: string, lang: string, deeplink?: string) => void;
  dismissToast: (id: string) => void;
  dismissConversation: (conv_id: string) => void;
  clearBadge: (section: NotifSection) => void;
}

export const useNotificationStore = create<NotificationState>()((set) => ({
  toasts: [],
  badges: { pairChat: 0, streetChat: 0 },

  dispatch(payload, pathname) {
    let conv_id = "";
    let conv_type = "";
    let senderName = "StreetChat";
    let text = "New message";

    let suppress = false;
    try {
      const raw =
        (payload.data as Record<string, string> | undefined)?.content ?? "{}";
      const msg = JSON.parse(raw);
      conv_id = String(msg.conv_id ?? "");
      conv_type = String(msg.conv_type ?? "");
      senderName = msg.sender_name || "StreetChat";
      const myUserId = useAuthStore.getState().userId;
      if (myUserId && msg.sender_uuid && msg.sender_uuid === myUserId)
        suppress = true;
      // Shared body rule: noti_text ?? text; suppress silent payloads; file fallback otherwise
      const display = msg.noti_text || msg.text;
      const hasAttachment =
        Array.isArray(msg.attachment?.media) && msg.attachment.media.length > 0;
      if (display) text = display;
      else if (!hasAttachment) suppress = true;
      // Delete a sent message: the live `deletion` broadcast is never a notification.
      if (msg.deletion) suppress = true;
      // Only a known event (a reaction's noti_text) or a message with content notifies: an unknown event never does.
      if (!msg.reaction && !hasMessageContent(msg)) suppress = true;
    } catch {
      // malformed payload — use fallbacks, never suppress
    }

    if (suppress) return;
    if (isSuppressed(conv_type, conv_id, pathname)) return;

    const section = resolveSection(conv_type);
    playChatSound(section);
    // The FCM data field `deeplink`, when present and safe, wins over the conversation.
    const deeplink = (payload.data as Record<string, string> | undefined)?.deeplink;
    const navigateTo = notiDeeplinkToPath(deeplink) ?? resolveNavigateTo(conv_type, conv_id);

    set((state) => {
      const badges = { ...state.badges, [section]: state.badges[section] + 1 };
      const current = state.toasts[0];
      if (current && sameGroup(current, conv_id, conv_type)) {
        return {
          badges,
          toasts: [{ ...current, senderName, text, count: current.count + 1 }],
        };
      }
      if (state.toasts.length === 0) {
        return {
          badges,
          toasts: [
            {
              id: crypto.randomUUID(),
              conv_id,
              conv_type,
              senderName,
              text,
              navigateTo,
              count: 1,
            },
          ],
        };
      }
      return { badges };
    });
  },

  dispatchAppNoti(noti, pathname, lang, deeplink) {
    if (noti.visible === false) return;
    const text = getLocalizedText(noti.message, lang);
    if (!text) return;
    const navigateTo =
      notiDeeplinkToPath(deeplink) ?? notiHrefToPath(noti.href) ?? NOTIFICATIONS_FALLBACK_PATH;
    // Already on that page: no toast, unless the link opens something there (Apopilo ?rate= / ?requests=).
    const [navPath, navQuery = ""] = navigateTo.split("?");
    if (pathname === navPath && !/(^|&)(rate|requests)=/.test(navQuery)) return;
    set((state) =>
      state.toasts.length === 0
        ? {
            toasts: [
              {
                id: crypto.randomUUID(),
                conv_id: "",
                conv_type: "",
                senderName: "StreetChat",
                text,
                navigateTo,
                count: 1,
              },
            ],
          }
        : {},
    );
  },

  dismissToast(id) {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },

  dismissConversation(conv_id) {
    if (!conv_id) return;
    set((state) => ({
      toasts: state.toasts.filter((t) => t.conv_id !== conv_id),
    }));
  },

  clearBadge(section) {
    set((state) => ({ badges: { ...state.badges, [section]: 0 } }));
  },
}));
